/**
 * ============================================================
 * Wingman RC5 — Indoor Airport Intelligence
 * IndoorTopologyContracts.ts
 *
 * Topology graph contracts: node, edge, audit report types.
 * Designed for HYBRID_NAVMESH_WITH_REGION_ADJACENCY model.
 *
 * IAM-M16B-R1.5 — Corridor + Door + Connector Topology
 * ============================================================
 */

// ── Geometry Primitives ──────────────────────────────────────

export type LonLat = readonly [number, number];

export enum TopologyNodeType {
  CORRIDOR = 'corridor',
  DOOR = 'door',
  CONNECTOR = 'connector',
  GATE = 'gate',
  SECURITY = 'security',
  POI = 'poi',
}

export enum TopologyEdgeType {
  /** Same-level walkable adjacency */
  ADJACENCY = 'adjacency',
  /** Door connects two corridors */
  DOOR_CONNECTION = 'door_connection',
  /** Vertical transition (escalator, elevator, stairs) */
  LEVEL_TRANSITION = 'level_transition',
}

// ── Graph Nodes ──────────────────────────────────────────────

export interface TopologyNode {
  /** Unique node ID (e.g., "corridor-42", "gate-B20") */
  readonly id: string;
  /** Node type classification */
  readonly type: TopologyNodeType;
  /** [longitude, latitude] centroid */
  readonly coordinates: LonLat;
  /** Floor level */
  readonly level: number;
  /** Human-readable label */
  readonly label: string;
  /** Optional: connected floor IDs for connectors */
  readonly connectedFloorIds?: readonly number[];
}

// ── Graph Edges ──────────────────────────────────────────────

export interface TopologyEdge {
  /** Source node ID */
  readonly from: string;
  /** Target node ID */
  readonly to: string;
  /** Edge type */
  readonly type: TopologyEdgeType;
  /** Distance in meters (Haversine) */
  readonly distanceMeters: number;
  /** Level transition penalty in meters (0 for same-level) */
  readonly penalty: number;
  /** Total weight = distanceMeters + penalty */
  readonly weight: number;
}

// ── Topology Graph ───────────────────────────────────────────

export interface TopologyGraph {
  readonly nodes: ReadonlyMap<string, TopologyNode>;
  readonly edges: readonly TopologyEdge[];
  readonly adjacencyList: ReadonlyMap<string, readonly TopologyEdge[]>;
  readonly nodeCount: number;
  readonly edgeCount: number;
}

// ── Audit Report ─────────────────────────────────────────────

export interface TopologyAuditReport {
  readonly airport: string;
  readonly terminal: string;
  readonly timestamp: number;
  readonly corridorCount: number;
  readonly doorCount: number;
  readonly connectorCount: number;
  readonly nodeCount: number;
  readonly edgeCount: number;
  readonly levels: readonly number[];
  readonly connectedComponents: number;
  readonly isFullyConnected: boolean;
  readonly reachabilityMatrix: ReadonlyMap<string, ReadonlySet<string>>;
}

// ── Distance Policy ──────────────────────────────────────────

export interface DistancePolicy {
  /** Maximum snap distance for same-level adjacency: corridor↔corridor, POI↔corridor (meters) */
  readonly sameLevelSnapMeters: number;
  /** Maximum snap distance for cross-level connector-to-non-connector attachment (meters) */
  readonly crossLevelSnapMeters: number;
  /**
   * Maximum horizontal distance for cross-floor connector-to-connector pairing (meters).
   * Used ONLY for matching two connector representations that describe the same
   * physical vertical transition structure (e.g., escalator top ↔ escalator bottom).
   * This MUST be independent of sameLevelSnapMeters.
   */
  readonly crossFloorPairMeters: number;
  /** Penalty added for each level transition (meters equivalent) */
  readonly levelTransitionPenaltyMeters: number;
}

export const PRODUCTION_DISTANCE_POLICY: DistancePolicy = {
  sameLevelSnapMeters: 35,
  crossLevelSnapMeters: 10,
  crossFloorPairMeters: 15,
  levelTransitionPenaltyMeters: 30,
} as const;

// ── Builder Interface ────────────────────────────────────────

export interface ITopologyBuilder {
  /** Build topology graph from raw geometry features */
  buildGraph(
    corridors: readonly TopologyNode[],
    doors: readonly TopologyNode[],
    connectors: readonly TopologyNode[],
    policy: DistancePolicy,
  ): TopologyGraph;

  /** Produce an audit report for the built graph */
  audit(graph: TopologyGraph): TopologyAuditReport;
}
